import React, { useState } from "react";
import { Table, Input, Progress, Typography, Tag } from "antd";
import { useIntl } from "react-intl";
import { SearchOutlined } from "@ant-design/icons";
import { IQuestionCategoryScore } from "../../services/nestjs-base-api/types";

const { Text } = Typography;

interface QuestionCategoriesTableProps {
    categories: IQuestionCategoryScore[];
}

const QuestionCategoriesTable: React.FunctionComponent<QuestionCategoriesTableProps> = ({
    categories,
}) => {
    const intl = useIntl();
    const [searchText, setSearchText] = useState("");

    const getScoreColor = (score: number): string => {
        if (score >= 70) return "#52c41a"; // Green
        if (score >= 40) return "#faad14"; // Orange
        return "#ff4d4f"; // Red
    };

    const getPerformanceLevel = (score: number): { text: string; color: string } => {
        if (score >= 70) return { 
            text: intl.formatMessage({ id: 'brandEquity.performance.high' }), 
            color: 'success' 
        };
        if (score >= 40) return { 
            text: intl.formatMessage({ id: 'brandEquity.performance.medium' }), 
            color: 'warning' 
        };
        return { 
            text: intl.formatMessage({ id: 'brandEquity.performance.low' }), 
            color: 'error' 
        };
    };

    const filteredCategories = categories.filter(category =>
        category.category.toLowerCase().includes(searchText.toLowerCase())
    );

    const columns = [
        {
            title: intl.formatMessage({ id: 'brandEquity.category.name' }),
            dataIndex: 'category',
            key: 'category',
            sorter: (a: IQuestionCategoryScore, b: IQuestionCategoryScore) => 
                a.category.localeCompare(b.category),
            render: (text: string) => (
                <Text strong style={{ fontSize: '14px' }}>
                    {text}
                </Text>
            ),
        },
        {
            title: intl.formatMessage({ id: 'brandEquity.category.meanScore' }),
            dataIndex: 'meanScore',
            key: 'meanScore',
            sorter: (a: IQuestionCategoryScore, b: IQuestionCategoryScore) => 
                (a.meanScore || 0) - (b.meanScore || 0),
            render: (score: number | null) => {
                if (score === null || score === undefined) {
                    return (
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                            N/A
                        </Text>
                    );
                }
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Progress
                            percent={score}
                            strokeColor={getScoreColor(score)}
                            size="small"
                            style={{ flex: 1, minWidth: '80px' }}
                            showInfo={false}
                        />
                        <Text strong style={{ color: getScoreColor(score), minWidth: '40px' }}>
                            {score.toFixed(1)}%
                        </Text>
                    </div>
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'brandEquity.category.performance' }),
            dataIndex: 'meanScore',
            key: 'performance',
            sorter: (a: IQuestionCategoryScore, b: IQuestionCategoryScore) => 
                (a.meanScore || 0) - (b.meanScore || 0),
            render: (score: number | null) => {
                if (score === null || score === undefined) {
                    return (
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                            N/A
                        </Text>
                    );
                }
                const performance = getPerformanceLevel(score);
                return (
                    <Tag color={performance.color}>
                        {performance.text}
                    </Tag>
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'brandEquity.category.responseCount' }),
            dataIndex: 'responseCount',
            key: 'responseCount',
            sorter: (a: IQuestionCategoryScore, b: IQuestionCategoryScore) => 
                a.responseCount - b.responseCount,
            render: (count: number) => (
                <Text style={{ fontSize: '14px' }}>
                    {count.toLocaleString()}
                </Text>
            ),
        },
    ];

    return (
        <div>
            {/* Search Filter */}
            <div style={{ marginBottom: '16px' }}>
                <Input
                    placeholder={intl.formatMessage({ id: 'brandEquity.category.search.placeholder' })}
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ maxWidth: '300px' }}
                />
            </div>

            {/* Summary Stats */}
            <div style={{ 
                marginBottom: '16px',
                padding: '12px 16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                display: 'flex',
                gap: '24px',
                flexWrap: 'wrap'
            }}>
                <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {intl.formatMessage({ id: 'brandEquity.category.total' })}
                    </Text>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>
                        {categories.length}
                    </div>
                </div>
                <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {intl.formatMessage({ id: 'brandEquity.category.averageScore' })}
                    </Text>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>
                        {categories.length > 0 
                            ? (() => {
                                const validScores = categories.filter(cat => cat.meanScore !== null && cat.meanScore !== undefined);
                                if (validScores.length === 0) return 'N/A';
                                const average = validScores.reduce((sum, cat) => sum + (cat.meanScore || 0), 0) / validScores.length;
                                return `${average.toFixed(1)}%`;
                            })()
                            : '0.0%'
                        }
                    </div>
                </div>
                <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {intl.formatMessage({ id: 'brandEquity.category.totalResponses' })}
                    </Text>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>
                        {categories.reduce((sum, cat) => sum + cat.responseCount, 0).toLocaleString()}
                    </div>
                </div>
            </div>

            {/* Categories Table */}
            <Table
                columns={columns}
                dataSource={filteredCategories.map((category, index) => ({
                    ...category,
                    key: index,
                }))}
                pagination={false}
                scroll={{ x: 800 }}
                size="middle"
            />
        </div>
    );
};

export default QuestionCategoriesTable;